// Regulatory copy, shared verbatim across every surface that shows it (the
// Expo result screen and the API's transactional/marketing emails). The
// wording is taken word-for-word from `docs/02-validation-plan.md §3.2` (the
// "lawyer-substitute" playbook) and is BINDING — do NOT paraphrase. Drift from
// information → advice is a regulatory kill-threat, not a style nit (see
// CLAUDE.md → Regulatory guardrails).
//
// This lives in @aucash/types precisely so there is one canonical string: the
// app and the email layer both import it, so the disclaimer can never silently
// diverge between channels. §3.2 also mandates this full block in the footer of
// *every* email, every time — the email templates depend on that guarantee.

export const SHORT_DISCLAIMER = "Educational tool. Not financial advice.";

// AU-08 (Private Health LHC + MLS) full block — verbatim from §3.2.
export const FULL_DISCLAIMER =
  "This tool provides educational information about how Lifetime Health Cover " +
  "loading and Medicare Levy Surcharge are calculated under current ATO and " +
  "APRA rules. The numbers shown are estimates based on the inputs you " +
  "provided and may not reflect your actual tax position. This is not " +
  "financial product advice. We do not recommend specific insurance products. " +
  "Before making any decision about private health insurance, consult an " +
  "authorised financial adviser, your tax agent, or contact your private " +
  "health insurer directly. Rules and thresholds change annually; always " +
  "verify against ato.gov.au and privatehealth.gov.au.";

// AU-06 (HECS/HELP) and AU-13 (CCS) full blocks. §3.2 only authored the AU-08
// wording (AU-08 was the only product then); rendering it on the HECS/CCS
// surfaces describes the wrong product + regulator. These mirror §3.2's exact
// sentence structure with the product/regulator specifics swapped — they carry
// the same BINDING weight and need the same lawyer sign-off as the AU-08 block
// (backlog: validate per-product disclaimers with the AFSL opinion).
export const FULL_DISCLAIMER_AU06 =
  "This tool provides educational information about how compulsory study and " +
  "training loan (HECS-HELP) repayments are calculated under current ATO rules. " +
  "The numbers shown are estimates based on the inputs you provided and may not " +
  "reflect your actual tax position. This is not financial or tax advice. " +
  "Before making any decision about your study and training loan, consult an " +
  "authorised financial adviser or your registered tax agent. Rules and " +
  "thresholds change annually; always verify against ato.gov.au.";

export const FULL_DISCLAIMER_AU13 =
  "This tool provides educational information about how the Child Care Subsidy " +
  "is calculated under current Services Australia and Department of Education " +
  "rules. The numbers shown are estimates based on the inputs you provided, may " +
  "not reflect your actual entitlement, and are not a Services Australia " +
  "assessment. This is not financial advice. Before claiming or changing your " +
  "subsidy, confirm your details with Services Australia. Rules and thresholds " +
  "change annually; always verify against servicesaustralia.gov.au.";

/** Pick the product-appropriate full disclaimer block. */
export function fullDisclaimerFor(product: "au08" | "au06" | "au13"): string {
  switch (product) {
    case "au06":
      return FULL_DISCLAIMER_AU06;
    case "au13":
      return FULL_DISCLAIMER_AU13;
    default:
      return FULL_DISCLAIMER;
  }
}
